import { StateBadge } from '@shared/ui/StateBadge';

import {
  formatEpisodeCount,
  formatFilmAssetCount,
  formatSeasonCount,
  resolveContentType,
} from '../_shared/constants';
import { toAcceptAttribute, type useUploadPolicy } from '../_shared/queries';
import { copy } from './copy';

/**
 * What the title holds now, plus the one destination that belongs to the title
 * itself rather than to anything under it.
 *
 * **A film's row for seasons is absent, not «٠».** A film has zero groups by
 * construction, and printing a zero would state a count for a level the record
 * does not have — which is the same fabrication a synthetic «الموسم ١» would be,
 * arrived at from the other direction.
 *
 * **The title-level subtitle track is a line, not a block.** A track that serves
 * the whole work rather than one asset is the rare case; the common one is a
 * track per episode, and that lives on the episode's own row. Giving the rare
 * case a full upload zone here would put the biggest control on the page in
 * front of the destination an operator almost never wants.
 */
export function ShapeCard({
  series,
  contentType,
  seasonCount,
  assetCount,
  uploadPolicy,
  onAddTitleDocument,
}: {
  series: boolean;
  contentType: string;
  seasonCount: number;
  /** Episodes for a series, and one-or-zero for a film. Counted by the caller,
   *  which is the side that knows which shape it is looking at. */
  assetCount: number;
  uploadPolicy: ReturnType<typeof useUploadPolicy>['data'];
  onAddTitleDocument: (file: File) => void;
}) {
  return (
    <section
      aria-labelledby="assets-shape"
      className="mb-8 grid gap-3 rounded-md border border-border bg-surface p-4"
    >
      <h2 id="assets-shape" className="font-display text-h3 font-bold">
        {copy.shape.heading}
      </h2>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        <dt className="text-ink-muted">{copy.shape.type}</dt>
        <dd className="mb-0">
          <StateBadge kind="neutral">{resolveContentType(contentType)}</StateBadge>
        </dd>

        {series ? (
          <>
            <dt className="text-ink-muted">{copy.shape.seasons}</dt>
            <dd className="mb-0">
              <bdi>{formatSeasonCount(seasonCount)}</bdi> — {copy.shape.seasonsNote}
            </dd>
          </>
        ) : null}

        <dt className="text-ink-muted">{copy.shape.assets}</dt>
        <dd className="mb-0">
          {/*
            The noun follows the shape. A film's file is a file — counting it as
            «حلقة واحدة» describes a one-episode series, which is exactly the
            synthetic season this card's own rule above refuses to draw.
          */}
          <bdi>{series ? formatEpisodeCount(assetCount) : formatFilmAssetCount(assetCount)}</bdi>
        </dd>
      </dl>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-ink-muted">{copy.shape.titleSubtitleLead}</span>
        <label className="hit-target inline-flex cursor-pointer items-center rounded-sm px-3 text-xs font-semibold text-ink hover:bg-hover-bg">
          {copy.shape.titleSubtitleAdd}
          <input
            type="file"
            className="sr-only"
            accept={toAcceptAttribute(uploadPolicy?.accepted_document_formats)}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onAddTitleDocument(file);
              event.target.value = '';
            }}
          />
        </label>
      </div>
    </section>
  );
}
